import { _js } from '@ifixit/utils/localize';
import { Button } from '@ifixit/ui/components/button';
import { FaIcon } from '@ifixit/ui/components/icon';
import { Input } from '@ifixit/ui/components/input';
import { cn } from '@ifixit/ui/lib/utils';
import {
   type Ref,
   useCallback,
   useEffect,
   useImperativeHandle,
   useLayoutEffect,
   useRef,
   useState,
} from 'react';
import { browseForDocument, type EditorDocument } from './document_browser';
import { SECTION_HEADING_CLASS } from './field';
import { IconActionButton } from './IconActionButton';
import { EmptyListNotice, LIST_ROW_CLASS, RemoveButton, RowIconLink } from './list_row';
import { unlessComposing } from './unless_composing';

// Mirrors DisplayDocument::getIcon() (Objects/DisplayDocument.php): only
// these four document types get a distinct icon there; everything else
// (e.g. 'access', '3d_model') falls back to the generic file icon.
const DOCUMENT_TYPE_ICONS: Record<string, string> = {
   word: 'file-word',
   excel: 'file-excel',
   powerpoint: 'file-powerpoint',
   pdf: 'file-pdf',
};

const GENERIC_DOCUMENT_ICON = 'file-lines';

// DocumentsAugment::MAX_TITLE_LENGTH, counted the way its mb_strlen counts:
// codepoints, not UTF-16 units. Past it validateTitle raises a generic
// invalid_value at save time, which names neither the field nor the document,
// so the limit is worth enforcing where the typing happens.
const MAX_TITLE_LENGTH = 255;

// The ternary is for the type checker, not for runtime: indexing a
// Record<string, string> with string | undefined is TS2538. `??` alone would
// behave identically on '' and undefined.
function documentIcon(documentType: string | undefined): string {
   return (documentType ? DOCUMENT_TYPE_ICONS[documentType] : undefined) ?? GENERIC_DOCUMENT_ICON;
}

/**
 * The row shape this editor reads. Callers keep their own document type —
 * the guide `{documentid, text, documentType}`, the wiki a fuller record —
 * and adapt it to these display fields.
 */
export type DocumentRow = {
   documentid: number;
   title: string;
   /**
    * DisplayDocument::getDocumentType(), which picks the file-type icon.
    * Undefined falls back to the generic file icon.
    */
   documentType?: string;
   /**
    * DisplayDocument::getUrl(). A document attached this session has none,
    * so the row falls back to the id-only form of GuideURI::guideDocument's
    * /Document/{docid} route and stays openable before the page is saved.
    */
   url?: string;
   /**
    * What an empty title means. DocumentsAugment::validateTitle stores '' as
    * null and fillDocumentInfo reads it back as the filename, so clearing the
    * field is how a rename gets undone — the row has to be able to show what
    * it would revert to.
    */
   filename?: string;
};

/**
 * Reads the rows with an open rename applied, for a save that never blurs the
 * input (Ctrl+S). Mirrors WikiInfoEditHandle.getFormValues.
 */
export type DocumentsFieldHandle<T extends DocumentRow = DocumentRow> = {
   getDocuments: () => T[];
};

/**
 * The attached-documents editor shared by the guide and wiki v2 editors: a
 * list of file-type-iconed rows that open in a new tab, with click-to-rename
 * and silent-immediate removal, over the shared media-library picker.
 *
 * Generic over the caller's row type so extra fields the editor never reads
 * (the wiki's `filename`, which its save payload strips) survive a rename.
 */
export function DocumentsField<T extends DocumentRow>({
   heading,
   documents,
   onChange,
   createRow,
   canRename = false,
   apiRef,
   onModified,
}: {
   heading: string;
   documents: T[];
   onChange: (documents: T[]) => void;
   /** Builds a caller-shaped row from the media library's picked document. */
   createRow: (picked: EditorDocument) => T;
   /**
    * Whether a renamed title survives a save. False for the guide, whose
    * PATCH takes a bare documentid array (GuidesApi::updateGuideDocuments)
    * and has nowhere to put a title.
    */
   canRename?: boolean;
   /** Exposes the live rows, so a save can send an uncommitted rename. */
   apiRef?: Ref<DocumentsFieldHandle<T>>;
   /**
    * Fires whenever the live rows may have changed, including per keystroke of
    * an open rename. The page recomputes its dirty bit by reading `apiRef`,
    * the way it already does for the info and supplier tables.
    */
   onModified?: () => void;
}) {
   const [editingId, setEditingId] = useState<DocumentRow['documentid'] | null>(null);
   const [draftTitle, setDraftTitle] = useState('');

   // Committing unmounts the input while it holds focus, which would drop the
   // caret to <body> mid-form, so a deliberate exit hands focus back to the
   // control that opened the edit.
   const renameButtonsRef = useRef(new Map<DocumentRow['documentid'], HTMLButtonElement>());

   // Mirrors editingId synchronously. Handing focus back to the button blurs
   // the still-mounted input, and blur is a commit, so an Escape would save
   // the draft it was cancelling; clearing this first makes that blur a no-op
   // without depending on when React flushes or when a frame lands.
   //
   // ItemRow solves the same re-entrant blur by deferring its refocus with
   // requestAnimationFrame instead. That was this component's first version
   // too, and the frame outlived the test that scheduled it, blurring a later
   // test's input; the synchronous mirror has no timing to leak.
   const editingIdRef = useRef<DocumentRow['documentid'] | null>(null);

   const endRename = (
      documentid: DocumentRow['documentid'],
      { shouldRefocus }: { shouldRefocus: boolean }
   ) => {
      editingIdRef.current = null;
      setEditingId(null);
      if (shouldRefocus) {
         renameButtonsRef.current.get(documentid)?.focus();
      }
   };

   // Every mutation reads the list as it stands rather than as it rendered:
   // the picker roundtrip and an open rename both span renders, and an index
   // or array captured at render time would resurrect a row removed
   // mid-flight or write a title onto whichever row took its place.
   //
   // Layout, not passive: the picker resolves in a microtask, while a passive
   // effect is a scheduler task, so a commit landing while the picker resolves
   // would not be guaranteed to have updated the ref first.
   const documentsRef = useRef(documents);
   useLayoutEffect(() => {
      documentsRef.current = documents;
   }, [documents]);

   const addDocument = async () => {
      const picked = await browseForDocument();
      if (!picked) {
         return;
      }
      const current = documentsRef.current;
      // A duplicate is never intended and would collide with the
      // documentid-keyed rows.
      if (current.some(doc => doc.documentid === picked.documentid)) {
         return;
      }
      onChange([...current, createRow(picked)]);
   };

   const removeDocument = (documentid: DocumentRow['documentid']) => {
      onChange(documentsRef.current.filter(doc => doc.documentid !== documentid));
   };

   const startRename = (doc: T) => {
      editingIdRef.current = doc.documentid;
      setEditingId(doc.documentid);
      setDraftTitle(doc.title);
   };

   /**
    * Deliberate exits (Enter, the check button) may commit an empty title:
    * that is how a rename is undone, since DocumentsAugment stores '' as null
    * and reads it back as the filename. Blur may not — clearing the field and
    * clicking away is far more likely to be an abandoned edit than a reset.
    */
   const commitRename = ({ isBlurExit = false } = {}) => {
      const editedId = editingIdRef.current;
      if (editedId === null) {
         return;
      }
      endRename(editedId, { shouldRefocus: !isBlurExit });
      const title = draftTitle.trim();
      if (isBlurExit && !title) {
         return;
      }
      const current = documentsRef.current;
      const target = current.find(doc => doc.documentid === editedId);
      // An untouched draft is compared before trimming, so opening a rename on
      // a title stored with padding and pressing Enter isn't an edit.
      // validateTitle doesn't trim either, so padded titles are storable.
      if (!target || draftTitle === target.title || target.title === title) {
         return;
      }
      onChange(current.map(doc => (doc.documentid === editedId ? { ...doc, title } : doc)));
   };

   const cancelRename = () => {
      const editedId = editingIdRef.current;
      if (editedId !== null) {
         endRename(editedId, { shouldRefocus: true });
      }
   };

   /**
    * The rows as they stand, with an open rename applied — the same value
    * Enter would commit. A draft lives in this component until something
    * commits it, and Ctrl+S saves without moving focus, so a page reading
    * `documents` alone sends the pre-rename title and loses what was typed.
    * Reporting a dirty bit isn't enough: the page needs the value too, which
    * is how the info and supplier tables already work (getFormValues).
    */
   const documentsWithOpenRename = (): T[] => {
      // Gated on canRename so a caller flipping it false mid-edit strands a
      // stale editingId harmlessly instead of applying a phantom draft.
      const editedRow =
         canRename && editingId !== null
            ? documents.find(doc => doc.documentid === editingId)
            : undefined;
      // Compared before trimming: a draft nobody typed into is not an edit,
      // even when the stored title carries padding validateTitle allowed.
      if (!editedRow || draftTitle === editedRow.title) {
         return documents;
      }
      const title = draftTitle.trim();
      return documents.map(doc => (doc.documentid === editingId ? { ...doc, title } : doc));
   };
   useImperativeHandle(apiRef, () => ({ getDocuments: documentsWithOpenRename }));

   // The page derives its dirty bit from the same call, rather than trusting a
   // boolean from here: one source of truth, and it cannot disagree with what
   // a save would send. Fires per keystroke, as the tables' onModified does.
   useEffect(() => {
      onModified?.();
      // React clears the imperative handle before this cleanup runs, so an
      // unmount mid-draft (a tab switch that never blurs the input) recomputes
      // against a null handle instead of leaving the page dirty forever.
      return () => onModified?.();
   }, [draftTitle, editingId, documents, onModified]);

   return (
      <div>
         <h2 className={cn('mb-1', SECTION_HEADING_CLASS)}>{heading}</h2>
         {documents.length === 0 && (
            <EmptyListNotice icon={GENERIC_DOCUMENT_ICON}>
               {_js('No documents attached yet. Attach the first one below.')}
            </EmptyListNotice>
         )}
         <ul className="m-0 flex list-none flex-col gap-1 p-0">
            {documents.map(doc => {
               const isEditing = canRename && doc.documentid === editingId;
               return (
                  <li key={doc.documentid} className={cn(LIST_ROW_CLASS, 'text-sm')}>
                     <DocumentLink doc={doc} />
                     {isEditing ? (
                        <RenameInput
                           value={draftTitle}
                           placeholder={doc.filename}
                           onChange={setDraftTitle}
                           onCommit={() => commitRename()}
                           onBlur={() => commitRename({ isBlurExit: true })}
                           onCancel={cancelRename}
                        />
                     ) : (
                        <span className="line-clamp-2 min-w-0 flex-1">
                           {doc.title || doc.filename}
                        </span>
                     )}
                     {canRename && (
                        <IconActionButton
                           ref={node => {
                              const buttons = renameButtonsRef.current;
                              if (node) {
                                 buttons.set(doc.documentid, node);
                              } else {
                                 buttons.delete(doc.documentid);
                              }
                           }}
                           label={isEditing ? _js('Save document name') : _js('Rename document')}
                           icon={isEditing ? 'check' : 'pen-to-square'}
                           onClick={() => (isEditing ? commitRename() : startRename(doc))}
                           onMouseDown={isEditing ? event => event.preventDefault() : undefined}
                           className="size-7 shrink-0 text-gray-400 hover:bg-gray-100 hover:text-brand-600"
                        />
                     )}
                     <RemoveButton
                        label={_js('Remove document')}
                        onClick={() => removeDocument(doc.documentid)}
                     />
                  </li>
               );
            })}
         </ul>
         <Button
            type="button"
            className={cn('button-md button-outline', 'mt-2')}
            onClick={() => void addDocument()}
         >
            <FaIcon name="plus" className="mr-2 size-3.5" />
            {_js('Attach a document')}
         </Button>
      </div>
   );
}

/**
 * The row's file-type icon, doubling as the link that opens the file in a
 * new tab — the same size-7 slot and hover affordance as the remove button
 * on the other end of the row.
 */
function DocumentLink({ doc }: { doc: DocumentRow }) {
   return (
      <RowIconLink
         label={_js('Open document in a new tab')}
         href={doc.url ?? `/Document/${doc.documentid}`}
         icon={documentIcon(doc.documentType)}
      />
   );
}

function RenameInput({
   value,
   placeholder,
   onChange,
   onCommit,
   onBlur,
   onCancel,
}: {
   value: string;
   /** The filename an emptied title falls back to, so the reset is visible. */
   placeholder?: string;
   onChange: (value: string) => void;
   onCommit: () => void;
   onBlur: () => void;
   onCancel: () => void;
}) {
   // A stable ref callback runs on mount only, so typing doesn't re-select
   // the field on every keystroke.
   const focusOnMount = useCallback((input: HTMLInputElement | null) => {
      input?.focus();
      input?.select();
   }, []);

   return (
      <Input
         ref={focusOnMount}
         aria-label={_js('Document title')}
         placeholder={placeholder}
         className="min-w-0 flex-1"
         value={value}
         // Clamped here rather than with maxLength, which counts UTF-16 units
         // where DocumentsAugment's mb_strlen counts codepoints: an emoji
         // title would have stopped at half the length the server accepts.
         onChange={event => onChange([...event.target.value].slice(0, MAX_TITLE_LENGTH).join(''))}
         onBlur={onBlur}
         onKeyDown={unlessComposing(event => {
            if (event.key === 'Enter') {
               event.preventDefault();
               onCommit();
            } else if (event.key === 'Escape') {
               onCancel();
            }
         })}
      />
   );
}
